import clsx from 'clsx';
import { Check, Stethoscope, TriangleAlert, Undo2, UserCheck, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentsButton } from '../components/documents';
import { LeaveStatusPill, periodText, signedHours, useExtentText } from '../components/leave';
import { Button, Checkbox, EmptyState, ErrorBox, Field, Loading, Modal, Notice, PageHeader, Pill, Select, Tabs, Textarea, useToast } from '../components/ui';
import { d, dt, num, wd } from '../lib/format';
import { useM, useQ, useTabParam } from '../lib/hooks';
import { ScrollBox, Sentinel, useChunked } from '../components/virtual';
import { useMe } from '../lib/me';
import { rowCheck, useRangeSelection } from '../lib/selection';

/**
 * Abwesenheitsanträge: decisions on the absence requests of other people, separate from the approval of time
 * entries. Who sees what: holders of the right "Personal" all requests (sick reports only they confirm); an
 * approver set for a person (e.g. the team lead) the requests of that person except sickness. Documents (sick note,
 * registration) open in the page; opening documents of others is recorded.
 */
export function LeaveRequestsPage() {
  const { t } = useTranslation();
  const { me } = useMe();
  const [tab, setTab] = useTabParam(['open', 'decided'] as const);
  const q = useQ('leave.queue', {}, { enabled: me.leaveApprover });
  if (!me.leaveApprover) return <EmptyState>{t('leaveRequests.noAccess')}</EmptyState>;
  const count = q.data?.items.filter((i) => !i.own).length ?? 0;
  return (
    <div className="pb-16">
      <PageHeader title={t('leaveRequests.title')} subtitle={q.data?.hr ? t('leaveRequests.subtitleHr') : t('leaveRequests.subtitleApprover')} />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'open', label: count ? `${t('leaveRequests.open')} (${count})` : t('leaveRequests.open') },
          { id: 'decided', label: t('leaveRequests.decided') },
        ]}
      />
      {tab === 'open' ? <OpenRequests q={q} /> : <Decided />}
    </div>
  );
}

type Queue = ReturnType<typeof useQ<'leave.queue'>>;

function OpenRequests({ q }: { q: Queue }) {
  const { t } = useTranslation();
  const toast = useToast();
  const extent = useExtentText();
  const inv = { invalidate: ['leave.', 'absences.', 'time.', 'kpi.', 'workload.', 'dashboard.'] };
  const decide = useM('leave.decide', inv);
  const undo = useM('leave.undoDecision', inv);
  const [kind, setKind] = useState('');
  const [person, setPerson] = useState('');
  const all = q.data?.items ?? [];
  const items = useMemo(() => all.filter((i) => (!kind || i.kind === kind) && (!person || i.personId === person)), [all, kind, person]);
  const chunk = useChunked(items, 30, `${kind}|${person}`);
  const decidable = useMemo(() => items.filter((i) => i.canDecide).map((i) => i.id), [items]);
  const sel = useRangeSelection(decidable);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const kinds = [...new Set(all.map((i) => i.kind))];
  const persons = [...new Map(all.map((i) => [i.personId, i.personName])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const reasons = (skipped: { reason: string }[]) => [...new Set(skipped.map((x) => x.reason))].map((c) => t(`errors.${c}` as 'errors.CONFLICT')).join(' ');
  const run = async (decision: 'approve' | 'change_request', ids: string[], note?: string) => {
    try {
      const r = await decide.mutateAsync({ ids, decision, note: note ?? null });
      const done = ids.filter((id) => !r.skipped.some((x) => x.id === id));
      toast.undoable(t(decision === 'approve' ? 'leave.approvedMany' : 'leave.rejectedMany', { count: r.done }), t('common.undo'), () =>
        undo
          .mutateAsync({ ids: done })
          .then((u) => {
            toast.ok(t('leave.undone', { count: u.undone }));
            if (u.skipped.length) toast.warn(`${t('approvals.undoSkipped', { count: u.skipped.length })} ${reasons(u.skipped)}`);
          })
          .catch(toast.error),
      );
      if (r.skipped.length) toast.warn(`${t('approvals.skipped', { count: r.skipped.length })} ${reasons(r.skipped)}`);
      sel.clear();
    } catch (e) {
      toast.error(e);
    }
  };
  const unit = (u: string) => t(`leave.units.${u as 'days'}`);
  return (
    <div className="flex flex-col gap-3">
      {all.length > 0 && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-line bg-sheet p-3">
          <Field label={t('leave.kind')} className="w-56">
            <Select className="h-8" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {t(`week.absence.${k as 'vacation'}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('common.person')} className="w-64">
            <Select className="h-8" value={person} onChange={(e) => setPerson(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {persons.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>
          {decidable.length > 1 && (
            <Checkbox className="mb-1.5" label={t('common.selectAll')} checked={sel.all} onChange={sel.toggleAll} />
          )}
          <span className="mb-1.5 ml-auto text-xs text-graphite">{t('leaveRequests.provisionalHint')}</span>
        </div>
      )}
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && items.length === 0 && <EmptyState>{t('leave.queueEmpty')}</EmptyState>}
      <ScrollBox maxHeight="min(74vh, 60rem)" label={t('leaveRequests.open')}>
      <ul className="flex flex-col gap-2">
        {chunk.visible.map((i) => {
          const restText =
            i.overtimeAfterMinutes !== null
              ? `${signedHours(i.overtimeAfterMinutes)} h ${t('leave.overtimeShort')}`
              : i.balances.length
                ? i.balances.map((b) => `${num(b.value)} ${unit(i.unit)}${i.balances.length > 1 ? ` (${b.year})` : ''}`).join(' / ')
                : null;
          const negative = (i.overtimeAfterMinutes ?? 0) < 0 || i.balances.some((b) => b.value < 0);
          return (
            <li key={i.id} className={clsx('rounded-lg border bg-sheet p-3', sel.selected.has(i.id) ? 'border-accent' : 'border-line')}>
              <div className="flex flex-wrap items-start gap-3">
                {i.canDecide && <Checkbox className="mt-1" label={<span className="sr-only">{t('common.select')}</span>} {...rowCheck(sel, i.id)} />}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{i.personName}</span>
                    <span className="text-xs text-graphite">{i.country}</span>
                    <Pill tone={i.health ? 'warn' : 'accent'}>
                      {i.health && <Stethoscope className="size-3" aria-hidden />}
                      {t(`week.absence.${i.kind as 'vacation'}`)}
                    </Pill>
                    {i.health && <span className="text-xs text-graphite">{t('leaveRequests.healthNote')}</span>}
                    {i.own && <Pill>{t('leave.own')}</Pill>}
                  </div>
                  <div className="num mt-1 text-sm">
                    {i.from === i.to ? `${wd(i.from)} ${d(i.from)}` : `${wd(i.from)} ${d(i.from)} - ${wd(i.to)} ${d(i.to)}`} · {extent(i)}
                  </div>
                  <div className="mt-0.5 text-xs text-graphite">
                    {t('leave.requestedAt')} {dt(i.requestedAt)}
                    {i.approverName && !i.health ? ` · ${t('leaveRequests.approverIs', { name: i.approverName })}` : ` · ${t('leaveRequests.approverHr')}`}
                  </div>
                  {i.note && <div className="mt-1 text-sm">„{i.note}“</div>}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    {restText && (
                      <span className={clsx('num rounded border px-1.5 py-0.5', negative ? 'border-bad/40 font-semibold text-bad' : 'border-line text-graphite')} title={t('leave.restAfterHint')}>
                        {t('leave.restAfter')}: {restText}
                      </span>
                    )}
                    <DocumentsButton absenceId={i.id} count={i.documents} expected={i.documentExpected} title={`${i.personName}: ${t(`week.absence.${i.kind as 'vacation'}`)} ${periodText(i.from, i.to)}`} />
                    {i.overlapsTeam.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-warn">
                        <TriangleAlert className="size-3.5" aria-hidden />
                        {t('leave.alsoAbsent', { names: i.overlapsTeam.join(', ') })}
                      </span>
                    )}
                    {i.bookedMinutes > 0 && (
                      <span className="inline-flex items-center gap-1 text-warn">
                        <TriangleAlert className="size-3.5" aria-hidden />
                        {t('leave.bookedInPeriod', { hours: num(i.bookedMinutes / 60) })}
                      </span>
                    )}
                  </div>
                </div>
                {i.canDecide && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="primary" icon={<Check className="size-3.5" />} loading={decide.isPending && decide.variables?.ids.includes(i.id)} onClick={() => run('approve', [i.id])}>
                      {i.health ? t('leaveRequests.confirm') : t('leave.approve')}
                    </Button>
                    <Button
                      size="sm"
                      icon={<X className="size-3.5" />}
                      onClick={() => {
                        sel.clear();
                        sel.toggle(i.id, true);
                        setReason('');
                        setRejectOpen(true);
                      }}
                    >
                      {t('leave.reject')}
                    </Button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {chunk.hasMore && <Sentinel onMore={chunk.more} />}
      </ScrollBox>
      {chunk.hasMore && <p className="text-xs text-graphite">{t('entries.showing', { shown: chunk.shown, total: chunk.total })}</p>}
      {sel.count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 flex flex-wrap items-center justify-center gap-2 border-t border-line bg-sheet/95 px-4 py-3 backdrop-blur lg:left-60">
          <span className="text-sm">{t('common.selected', { count: sel.count })}</span>
          <Button variant="primary" icon={<Check className="size-4" />} loading={decide.isPending} onClick={() => run('approve', [...sel.selected])}>
            {t('leave.approve')}
          </Button>
          <Button icon={<X className="size-4" />} onClick={() => (setReason(''), setRejectOpen(true))}>
            {t('leave.reject')}
          </Button>
        </div>
      )}
      <Modal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title={t('leave.rejectTitle')}
        footer={
          <>
            <Button onClick={() => setRejectOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="primary" disabled={reason.trim().length < 3} loading={decide.isPending} onClick={() => run('change_request', [...sel.selected], reason.trim()).then(() => setRejectOpen(false))}>
              {t('leave.reject')}
            </Button>
          </>
        }
      >
        <Field label={t('leave.rejectReason')}>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
        </Field>
      </Modal>
    </div>
  );
}

function Decided() {
  const { t } = useTranslation();
  const toast = useToast();
  const extent = useExtentText();
  const q = useQ('leave.decided', {});
  const undo = useM('leave.undoDecision', { invalidate: ['leave.', 'absences.', 'time.', 'kpi.', 'workload.', 'dashboard.'] });
  const items = q.data?.items ?? [];
  const chunk = useChunked(items, 50);
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="neutral">{t('leaveRequests.decidedHint')}</Notice>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && items.length === 0 && <EmptyState>{t('leaveRequests.noneDecided')}</EmptyState>}
      <ScrollBox maxHeight="min(72vh, 56rem)" label={t('leaveRequests.decided')} className="rounded-lg border border-line bg-sheet">
      <ul className="flex flex-col divide-y divide-line">
        {chunk.visible.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
            <UserCheck className="size-4 text-graphite" aria-hidden />
            <span className="min-w-40 font-medium">{a.personName}</span>
            <span>{t(`week.absence.${a.kind as 'vacation'}`)}</span>
            <span className="num text-graphite">
              {periodText(a.from, a.to)} · {extent(a)}
            </span>
            <LeaveStatusPill status={a.status} />
            {a.decisionNote && <span className="text-xs text-graphite">„{a.decisionNote}“</span>}
            <span className="ml-auto flex items-center gap-2">
              {a.documents > 0 && <DocumentsButton absenceId={a.id} count={a.documents} title={`${a.personName}: ${periodText(a.from, a.to)}`} />}
              <span className="num text-xs text-graphite">{dt(a.decidedAt)}</span>
              {a.canUndo && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Undo2 className="size-3.5" />}
                  loading={undo.isPending && undo.variables?.ids.includes(a.id)}
                  onClick={() =>
                    undo
                      .mutateAsync({ ids: [a.id] })
                      .then((u) => (u.undone ? toast.ok(t('leave.undone', { count: u.undone })) : toast.warn(t('approvals.undoSkipped', { count: u.skipped.length }))))
                      .catch(toast.error)
                  }
                >
                  {t('common.undo')}
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>
      {chunk.hasMore && <Sentinel onMore={chunk.more} />}
      </ScrollBox>
    </div>
  );
}
